/**
 * Analyzer / Monthly tag for a group.
 *
 * Lives in common-web because multiple surfaces render it and must agree on
 * the wording: the accounts list rows and kanban cards, the account detail
 * header, and the group details page. The designation is derived server-side
 * from `GroupMetadata.internalProcess` (see `@common/lib/groupProcessType`) —
 * analyzer groups run automated payroll processing, monthly groups are
 * invoiced monthly.
 *
 * `null`/`undefined` means there is no local Group row or its internalProcess
 * is unclassified, so the designation is unknown — no tag rather than a guess.
 */

import type {ReactElement} from 'react';
import type {GroupProcessType} from '@common/lib/groupProcessType';
import {Badge} from '@common-web/components/ui/badge';
import {cn} from '@common-web/lib/utils';

interface AnalyzerGroupBadgeProps {
  /** Derived from `GroupMetadata.internalProcess`; null/undefined when unknown. */
  groupProcessType: GroupProcessType | null | undefined;
  /** Dense form for list rows and kanban cards. */
  compact?: boolean;
  className?: string;
}

export function AnalyzerGroupBadge({
  groupProcessType,
  compact = false,
  className,
}: AnalyzerGroupBadgeProps): ReactElement | null {
  if (!groupProcessType) return null;

  const isAnalyzer = groupProcessType === 'ANALYZER';
  return (
    <Badge
      variant={isAnalyzer ? 'info' : 'outline'}
      size={compact ? 'sm' : 'default'}
      className={cn('whitespace-nowrap', className)}
      title={
        isAnalyzer
          ? 'Analyzer group — automated payroll processing'
          : 'Monthly group — invoiced monthly'
      }
    >
      {isAnalyzer ? 'Analyzer' : 'Monthly'}
    </Badge>
  );
}
